import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Briefcase, Globe, Layout, Code, Database, ShoppingCart } from "lucide-react";

// --- Helper Components ---

// StarRating Helper
const StarRating = ({ rating }: { rating: number }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    stars.push(
      <svg
        key={i}
        className={`h-4 w-4 ${
          i <= rating ? "text-yellow-500" : "text-gray-300" 
        } inline-block`}
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
      </svg>
    );
  }
  return <div className="flex items-center">{stars}</div>;
};

// Verified Badge
const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <svg
      className="w-3 h-3 mr-1 text-blue-600"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
    Verified
  </span>
);

// --- Tools Section (Split View: Design vs Development) ---
const ToolsYouWillMaster = () => {
  // 1. Common Basics
  const commonTools = [
    { name: 'WordPress 6.x', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/wordpress/wordpress-plain.svg' },
    { name: 'HTML5', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/html5/html5-original.svg' },
    { name: 'CSS3', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/css3/css3-original.svg' },
    { name: 'cPanel', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/CPanel_logo.svg/1200px-CPanel_logo.svg.png' }, // Custom URL for cPanel
  ];

  // 2. Option A: No-Code & Design (Elementor/Woo)
  const designTools = [
    { name: 'Elementor', logo: 'https://upload.wikimedia.org/wikipedia/commons/d/d3/Elementor-logo.png' }, // Custom
    { name: 'WooCommerce', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/woocommerce/woocommerce-original.svg' },
    { name: 'Figma', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg' },
    { name: 'Yoast SEO', logo: 'https://upload.wikimedia.org/wikipedia/commons/e/e5/Yoast_SEO_Logotype.svg' },
  ];

  // 3. Option B: Core Development (PHP/Theme Dev)
  const devTools = [
    { name: 'PHP', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/php/php-original.svg' },
    { name: 'MySQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mysql/mysql-original-wordmark.svg' },
    { name: 'JavaScript', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/javascript/javascript-original.svg' },
    { name: 'Bootstrap', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/bootstrap/bootstrap-original.svg' },
  ];

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 text-center">
        
        {/* Heading */}
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-black font-aligran">
          Tools You Will <span className="text-blue-600">Master</span>
        </h2>
        <p className="text-gray-600 mb-12 max-w-2xl mx-auto">
          We cover the entire ecosystem: From drag-and-drop design to custom backend coding.
        </p>

        {/* 1. CORE ESSENTIALS */}
        <div className="mb-12">
          <h3 className="text-lg font-bold text-gray-500 uppercase tracking-widest mb-6">Foundations (Compulsory)</h3>
          <div className="flex flex-wrap justify-center gap-6">
            {commonTools.map((tool, i) => (
              <motion.div
                key={i}
                className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all w-28 h-28"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <img src={tool.logo} alt={tool.name} className="w-10 h-10 mb-2 object-contain" />
                <p className="text-xs font-semibold text-gray-800">{tool.name}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* DIVIDER - DUAL POWER */}
        <div className="relative flex items-center justify-center mb-12">
          <div className="border-t border-gray-300 w-full max-w-xs absolute"></div>
          <span className="bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-bold z-10 shadow-lg tracking-wide">
            DESIGN + DEVELOPMENT
          </span>
        </div>

        {/* 2. SPLIT VIEW */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          
          {/* OPTION A: DESIGNER */}
          <div className="bg-white p-8 rounded-2xl border-2 border-dashed border-gray-200 hover:border-pink-500 transition-colors relative group">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-pink-100 text-pink-700 px-4 py-1 rounded-full text-xs font-bold border border-pink-200">
              NO-CODE & DESIGN
            </div>
            <h4 className="text-xl font-bold mb-6 text-gray-800">Page Builders & SEO</h4>
            <div className="flex justify-center gap-4 flex-wrap">
              {designTools.map((tool, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center mb-2 group-hover:bg-pink-50 transition">
                    <img src={tool.logo} alt={tool.name} className="w-8 h-8 object-contain" />
                  </div>
                  <span className="text-xs font-medium text-gray-600">{tool.name}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-4">Create stunning websites in minutes without code.</p>
          </div>

          {/* PLUS Circle */}
          <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 mt-20 items-center justify-center w-10 h-10 bg-gray-200 rounded-full text-gray-500 font-bold text-xs border-4 border-gray-50 z-10">
            &
          </div>

          {/* OPTION B: DEVELOPER */}
          <div className="bg-white p-8 rounded-2xl border-2 border-dashed border-gray-200 hover:border-blue-500 transition-colors relative group">
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-blue-100 text-blue-700 px-4 py-1 rounded-full text-xs font-bold border border-blue-200">
              CORE DEVELOPMENT
            </div>
            <h4 className="text-xl font-bold mb-6 text-gray-800">Theme & Plugin Dev</h4>
            <div className="flex justify-center gap-4 flex-wrap">
              {devTools.map((tool, i) => (
                <div key={i} className="flex flex-col items-center">
                  <div className="w-16 h-16 bg-gray-50 rounded-lg flex items-center justify-center mb-2 group-hover:bg-blue-50 transition">
                    <img src={tool.logo} alt={tool.name} className="w-8 h-8 object-contain" />
                  </div>
                  <span className="text-xs font-medium text-gray-600">{tool.name}</span>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500 mt-4">Build custom functionality and themes from scratch.</p>
          </div>

        </div>

      </div>
    </section>
  );
};


const WordPressDev = () => {
  // --- Data ---
  const testimonials = [
    {
      id: 1,
      name: "Simran Kaur",
      review:
        "I started as a freelancer after just 2 months. Elementor and WooCommerce modules are gold mines. I charge 20k per website now!",
      rating: 5,
      date: "2024-08-10",
      profileImg: "https://randomuser.me/api/portraits/women/44.jpg", 
      source: "Upwork",
    },
    {
      id: 2,
      name: "Rohit Mehta",
      review:
        "The PHP and Custom Theme Development module sets this course apart. Now I don't just install themes, I build them.",
      rating: 5,
      date: "2024-08-05",
      profileImg: "https://randomuser.me/api/portraits/men/32.jpg", 
      source: "Google Reviews",
    },
    {
      id: 3,
      name: "Ankit Dubey",
      review:
        "Best course for non-tech background. Started with No-Code tools, and now slowly learning PHP basics too. Very well structured.",
      rating: 4,
      date: "2024-07-28",
      profileImg: "https://randomuser.me/api/portraits/men/15.jpg", 
      source: "Website Feedback",
    },
    {
      id: 4,
      name: "Meera Joshi",
      review:
        "I built my own E-commerce store during the course. The instructor explains Payment Gateway integration very clearly.",
      rating: 5,
      date: "2024-07-22",
      profileImg: "https://randomuser.me/api/portraits/women/65.jpg",
      source: "Google Reviews",
    },
    {
      id: 5,
      name: "Vikram Singh",
      review:
        "Great for freelancing. They teach you how to bid on projects and handle clients along with technical skills.",
      rating: 4,
      date: "2024-07-15",
      profileImg: "https://randomuser.me/api/portraits/men/85.jpg",
      source: "LinkedIn",
    },
  ];

  const faqContent = [
    {
      title: "Is coding required to learn WordPress?",
      answer:
        "No and Yes. You can build professional websites using Elementor (No-Code). However, to become a 'Developer' and earn more, we teach PHP & CSS customization.",
    },
    {
      title: "Will I learn how to build E-commerce sites?",
      answer:
        "Yes, a major part of the course focuses on WooCommerce, payment gateway integration (Razorpay/Stripe), and product management.",
    },
    {
      title: "What is the difference between Elementor and Custom Theme?",
      answer:
        "Elementor is a drag-and-drop builder for quick designs. Custom Themes are coded from scratch using PHP for high performance. We teach both.",
    },
    {
      title: "Can I start freelancing after this course?",
      answer:
        "Absolutely! WordPress powers 43% of the web. We include a module on 'Freelancing' to help you get your first client on Upwork/Fiverr.",
    },
    {
      title: "Do you cover hosting and domain setup?",
      answer:
        "Yes, we teach you how to buy domains, set up cPanel, install SSL, and migrate websites from local to live servers.",
    },
  ];

  const curriculumContent = [
    {
      title: "Module 1: CMS Fundamentals & Dashboard",
      description: "Installation (Localhost/Live), Dashboard overview, Posts vs Pages, Categories, and Media management.",
    },
    {
      title: "Module 2: Theme Customization & Elementor",
      description: "Mastering Elementor Page Builder, working with Headers/Footers, and responsive design for mobile.",
    },
    {
      title: "Module 3: WooCommerce & E-Commerce",
      description: "Setting up a store, managing products, shipping zones, taxes, and payment gateway integration.",
    },
    {
      title: "Module 4: Core PHP & MySQL for WordPress",
      description: "Understanding PHP syntax, Loops, Arrays, and how WordPress interacts with the MySQL database.",
    },
    {
      title: "Module 5: Custom Theme Development",
      description: "Converting an HTML/CSS template into a fully functional dynamic WordPress theme from scratch.",
    },
    {
      title: "Module 6: Plugin Architecture & APIs",
      description: "How to create custom plugins, Shortcodes, and using Custom Post Types (CPT) for advanced functionality.",
    },
    {
      title: "Module 7: Speed Optimization & SEO",
      description: "Caching, Image optimization, Yoast/RankMath setup, and securing your WordPress site from hackers.",
    },
    {
      title: "Module 8: Freelancing & Client Management",
      description: "How to price projects, write proposals, migrate sites, and maintain client websites professionally.",
    },
  ];

  // --- Logic for Carousel ---
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setVisibleCount(3);
      else if (window.innerWidth >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const autoScrollInterval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(autoScrollInterval);
  }, [testimonials.length]);

  const currentVisibleTestimonials = [];
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(
      testimonials[(currentTestimonialIndex + i) % testimonials.length]
    );
  }

  // --- Logic for Form ---
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Thank you! We will contact you shortly regarding the WordPress course.");
    setFormData({ firstName: "", businessEmail: "", phone: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white text-gray-900 font-sans">
      <Helmet>
        <title>WordPress Development Course | Theme & Plugin Dev</title>
        <meta name="description" content="Master WordPress from Elementor to Custom PHP Theme Development." />
      </Helmet>

      {/* --- HERO SECTION --- */}
      <section className="relative overflow-hidden h-[550px] flex items-center bg-black"> 
      
      {/* Background Image + Gradient */}
      <div className="absolute inset-0 z-0">
        <img
          src="https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?q=80&w=2070&auto=format&fit=crop"
          alt="WordPress Workspace"
          className="w-full h-full object-cover opacity-30"
        />
        {/* Blue Gradient for WordPress Theme */}
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-blue-900/40" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10 items-center w-full pt-20">

        {/* Left Content */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="inline-block px-3 py-1 mb-4 border border-blue-500 rounded-full bg-blue-900/30 text-blue-400 text-sm font-semibold tracking-wide">
            FREELANCER'S FAVORITE
          </div>

          <h1 className="text-4xl md:text-5xl font-extrabold leading-tight mb-6 text-gray-100 font-aligran">
            Master WordPress
            <span className="text-blue-500 block">Development</span>
          </h1>

          <p className="text-xl text-gray-300 max-w-2xl mb-8">
            From <strong>No-Code</strong> designs with Elementor to <strong>Pro-Code</strong> custom themes with PHP. Power 43% of the web.
          </p>

          <button
            onClick={() =>
              document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })
            }
            className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-10 rounded-lg shadow-[0_5px_20px_rgba(37,99,235,0.3)] transition duration-300 transform hover:scale-105"
          >
            Start Building
          </button>
        </motion.div>

        {/* Right Side Image (WordPress Logo) */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.9 }}
          className="flex justify-center md:justify-end"
        >
          <img
            src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/wordpress/wordpress-plain.svg" 
            alt="WordPress Logo"
            className="w-72 md:w-80 
                       drop-shadow-[0_5px_30px_rgba(59,130,246,0.4)]
                       transition-transform duration-300 
                       hover:scale-105"
          />
        </motion.div>
      </div>
      </section>

      {/* --- WHY LEARN SECTION --- */}
      <section className="py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6 text-center">

          <h2 className="text-4xl md:text-5xl font-extrabold mb-4 text-gray-900 font-aligran">
            Why Learn <span className="text-blue-600">WordPress?</span>
          </h2>

          <p className="text-gray-600 mb-14 max-w-3xl mx-auto text-lg leading-relaxed">
            It's not just a blogging tool anymore. It's a full-fledged CMS powering major brands, e-commerce stores, and enterprise sites.
          </p>

          <div className="grid md:grid-cols-3 gap-8">

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Globe size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Powers 43% of Web</h3>
              <p className="text-gray-600 leading-relaxed">
                Almost half of the internet runs on WordPress. The job market and freelance opportunities are massive.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <Briefcase size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">Freelancing King</h3>
              <p className="text-gray-600 leading-relaxed">
                The easiest skill to start earning money quickly on Upwork, Fiverr, or by building local business sites.
              </p>
            </div>

            <div className="bg-white border border-gray-200 p-8 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 group">
              <div className="w-14 h-14 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-5 mx-auto group-hover:scale-110 transition">
                <ShoppingCart size={28} />
              </div>
              <h3 className="font-bold text-2xl text-black mb-3">E-Commerce Ready</h3>
              <p className="text-gray-600 leading-relaxed">
                With WooCommerce, you can build powerful online stores that rival Shopify, but with full control.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* --- HIGHLIGHTS SECTION --- */}
      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
            Course <span className="text-blue-600">Highlights</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: 'Live Projects', desc: 'Build a News Portal, Portfolio & E-commerce Site.', img: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f' },
              { title: 'Theme Development', desc: 'Learn to convert HTML to WordPress Theme.', img: 'https://images.unsplash.com/photo-1507238691740-187a5b1d37b8' },
              { title: 'Freelance Training', desc: 'Dedicated module on getting clients & pricing.', img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f' },
              { title: 'Elementor Pro', desc: 'Master the most popular Page Builder.', img: 'https://upload.wikimedia.org/wikipedia/commons/d/d3/Elementor-logo.png' },
              { title: 'WooCommerce', desc: 'Complete Online Store setup & payment gateway.', img: 'https://images.unsplash.com/photo-1556742049-0cfed4f7a07d' },
              { title: 'Security & SEO', desc: 'Secure sites from hacks & rank on Google.', img: 'https://images.unsplash.com/photo-1563986768609-322da13575f3' },
              { title: 'Custom PHP', desc: 'Learn the backend code that powers WP.', img: 'https://images.unsplash.com/photo-1599507593499-a3f7d7d97663' },
              { title: 'Certification', desc: 'Industry-recognized completion certificate.', img: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f' },
              { title: 'Support', desc: '24/7 doubt support with personal mentorship.', img: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden flex h-44 border-l-4 border-blue-600"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="w-1/2 p-5 flex flex-col justify-center">
                  <h3 className="font-bold text-lg text-black mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
                <div className="w-1/2 relative">
                  <img src={item.img} alt={item.title} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-l from-black/40 to-transparent"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* --- CURRICULUM SECTION --- */}
      <section className="py-20 bg-white"> 
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-left text-gray-900 font-aligran"> 
            Detailed <span className="text-blue-600">Curriculum</span>
          </h2>
          <div className="space-y-4">
            {curriculumContent.map((module, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"> 
                <details className="group">
                  <summary className="cursor-pointer px-6 py-4 font-semibold text-lg bg-gray-100 hover:bg-gray-200 transition flex justify-between items-center text-gray-900"> 
                    {module.title}
                    <span className="transition-transform group-open:rotate-180 text-gray-900">▼</span>
                  </summary>
                  <div className="px-6 py-4 text-black border-t border-gray-200 leading-relaxed"> 
                    <p>{module.description}</p>
                  </div>
                </details>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* --- TOOLS SECTION (SPLIT) --- */}
      <ToolsYouWillMaster />

      {/* --- FAQ & FORM SECTION --- */}
      <section className="max-w-7xl mx-auto px-6 py-20 grid md:grid-cols-3 gap-10 bg-white"> 
        <div className="md:col-span-2">
          <h2 className="text-3xl font-bold mb-8 text-gray-900 font-aligran">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqContent.map((item, idx) => (
              <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg"> 
                <details className="group p-4">
                  <summary className="cursor-pointer font-medium text-gray-900 hover:text-gray-700 flex justify-between">
                    {item.title}
                    <span className="text-gray-900 group-open:text-blue-600">+</span>
                  </summary>
                  <p className="mt-4 text-black text-sm border-t border-gray-200 pt-3">{item.answer}</p> 
                </details>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-1" id="enquiry-form">
          <div className="bg-gray-100 border border-blue-300 rounded-xl p-6 shadow-xl sticky top-10">
            <h3 className="text-2xl font-bold mb-2 text-center text-blue-600">Book Your Seat</h3>
            <p className="text-center text-gray-600 mb-6 text-sm">Start your Freelancing Journey.</p>
            <form onSubmit={handleSubmit} className="space-y-4">
              <input type="text" name="firstName" placeholder="Your Name" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-blue-500 focus:outline-none" value={formData.firstName} onChange={handleInputChange} />
              <input type="email" name="businessEmail" placeholder="Email Address" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-blue-500 focus:outline-none" value={formData.businessEmail} onChange={handleInputChange} />
              <input type="tel" name="phone" placeholder="Phone Number" required className="w-full bg-white border border-gray-300 rounded p-3 text-black focus:border-blue-500 focus:outline-none" value={formData.phone} onChange={handleInputChange} />
              <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 rounded transition shadow-lg">ENQUIRE NOW</button>
              <p className="text-xs text-center text-gray-500 mt-2">We respect your privacy.</p>
            </form>
          </div>
        </div>
      </section>

      {/* --- TESTIMONIALS SECTION --- */}
      <section className="bg-gray-50 py-20 border-t border-gray-200"> 
        <div className="max-w-7xl mx-auto px-6 text-left"> 
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 font-aligran">
                Success <span className="text-blue-600">Stories</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                    {currentVisibleTestimonials.map((testimonial) => (
                        <motion.div
                            key={testimonial.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            transition={{ duration: 0.4 }}
                            className="bg-white p-6 rounded-xl border border-gray-200 flex flex-col text-left hover:border-blue-400/50 transition shadow-sm"
                        >
                            <div className="flex items-center mb-4">
                                <img src={testimonial.profileImg} alt={testimonial.name} className="w-12 h-12 rounded-full border-2 border-blue-500 mr-3" />
                                <div>
                                    <h4 className="font-bold text-black">{testimonial.name}</h4> 
                                    <span className="text-gray-500 text-xs">{testimonial.source}</span>
                                </div>
                            </div>
                            <div className="mb-3"><StarRating rating={testimonial.rating} /></div>
                            <p className="text-gray-700 text-sm flex-grow">"{testimonial.review}"</p> 
                            <VerifiedBadge />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <div className="flex justify-start mt-8 space-x-2"> 
                {testimonials.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentTestimonialIndex(idx)}
                        className={`h-2 w-2 rounded-full transition-all ${idx === currentTestimonialIndex ? 'bg-blue-600 w-6' : 'bg-gray-300'}`}
                    />
                ))}
            </div>
        </div>
      </section>
    </div>
  );
};

export default WordPressDev;